
import { useState } from "react";
import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import axios from "axios";
import { ArrowLeft, ArrowRight } from "lucide-react";

import Breadcrumb from "./Breadcrumb";

import DependencyBoxLoading from "../loading/dependency-box";
import { useLanguage } from "../../store/language-context";
import { useTranslation } from "react-i18next";

// ✅ Pagination with Lucide Icons
const Pagination = ({ meta, onPageChange }) => {
  if (!meta) return null;

  return (
    <div className="flex justify-center items-center gap-2 mt-8 flex-wrap">
      {meta?.links?.map((link, index) => {
        const label = link.label;

        // Determine if it's previous/next
        const isPrev = label.includes("السابق");
        const isNext = label.includes("التالي");
        const isPage = !isPrev && !isNext;

        return (
          <button
            key={index}
            onClick={() => {
              if (link?.url) {
                const page = new URL(link?.url).searchParams.get("page");
                onPageChange(Number(page));
              }
            }}
            disabled={!link.url}
            className={`px-2 py-1 rounded text-sm flex items-center justify-center
              ${link.active ? 'border-2 border-secondary-tw text-secondary-tw' : 'bg-white text-secondary-tw'}
              ${!link.url ? 'opacity-70 cursor-not-allowed ' : 'hover:bg-gray-200'}
            `}
          >
            {isPrev &&
              <span className="border-2 border-secondary-tw text-secondary-tw px-2 py-1 rounded-lg">
                <ArrowRight size={16} />
              </span>
            }
            {isNext && <span className="border-2 border-secondary-tw text-secondary-tw px-2 py-1 rounded-lg">
              <ArrowLeft size={16} />
            </span>}
            {isPage && <span dangerouslySetInnerHTML={{ __html: label }} />}
          </button>
        );
      })}
    </div>
  );
};

const Dependencies = () => {
  const { URL } = useLanguage();
  const {t} = useTranslation();
  const [seeFilter, setSeeFilter] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);

  const { data, isLoading, error } = useQuery({
    queryKey: ['dependencies-page', currentPage, URL],
    queryFn: async () => {
      try {
        const response = await axios.get(`${URL}/dependencies?per_page=12&page=${currentPage}`);
        if (response.status !== 200) throw new Error(t('custom.err'))
        return response?.data?.data;
      } catch (error) {
        throw new Error(t('custom.err'))
      }
    },
    staleTime: 1000 * 60 * 60 * 6, // 6HR
    gcTime: 1000 * 60 * 60 * 6,
    refetchOnWindowFocus: false,
    refetchOnMount: false,
    refetchOnReconnect: false,
    retry: 1,
  });

  const partners = data?.data;
  const meta = data?.meta;

  if (error) {
    return (
      <div className="h-dvh w-full flex justify-center items-center">
        <p>{error?.message || t('custom.err')}</p>
      </div>
    );
  }

  return (
    <>
      <Breadcrumb pages={[t('routes.accreditations')]} />
      <main className="container">
        <section className="flex items-start gap-3 max-md:flex-col mb-5">

          <div className={`grid w-full gap-4 p-3 justify-items-center ${seeFilter ? "grid-cols-2 max-xl:grid-cols-1" : "grid-cols-4 max-lg:grid-cols-2"} max-md:grid-cols-1`}>
            {isLoading && <DependencyBoxLoading />}
            {partners?.map((partner) => (
              <div
                key={partner?.id}
                className="bg-white dependance-card w-full transition overflow-hidden p-0 rounded-xl my-3 mx-1"
              >
                <div className="rounded-card-image w-100"></div>
                <img src={partner?.image} alt={partner?.name} className="rounded-card-img" />
                <p className="text-lg p-text">{partner?.title}</p>
                <Link to={`/dependencies/${partner?.id}`} className="see-more-btn">
                  {t('custom.show')} ←
                </Link>
              </div>
            ))}
          </div>
        </section>

        {/* Pagination */}
        {meta && (
          <Pagination meta={meta} onPageChange={(page) => setCurrentPage(page)} />
        )}
      </main>
    </>
  );
};

export default Dependencies;
